import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonButton,
  IonContent,
  IonGrid,
  IonIcon,
  IonBadge,
  IonAccordionGroup,
  IonAccordion,
  IonItem,
  IonLabel,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardContent,
} from '@ionic/react';
import {
  closeOutline,
  copyOutline,
  chevronForwardOutline,
} from 'ionicons/icons';
import { FaUniversity } from 'react-icons/fa';
import styles from './HelcimTransactionDetailModal.module.scss';
import BasePageWithHeader from './BasePageWithHeader';
import { getHelcimCustomer } from '../services/helcimInvoicingService';
import { useTranslation } from 'react-i18next';

function pick(tx: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = String(tx[k] ?? '').trim();
    if (v) return v;
  }
  const card = tx['card'] as Record<string, unknown> | undefined;
  if (card && typeof card === 'object') {
    for (const k of keys) {
      const v = String(card[k] ?? '').trim();
      if (v) return v;
    }
  }
  return '';
}

function formatCardNumber(tx: Record<string, unknown>): string {
  const raw = pick(tx, 'cardNumber', 'card_number', 'maskedPan', 'pan');
  if (raw.length === 10) {
    const f6 = raw.slice(0, 6);
    const l4 = raw.slice(6);
    return `${f6.slice(0, 4)} ${f6.slice(4, 6)}** **** ${l4}`;
  }
  const f6 = pick(tx, 'cardF6', 'cardBin', 'first6');
  const l4 = pick(tx, 'cardSuffix', 'cardL4', 'cardLast4', 'last4', 'cardEnd');
  if (f6.length >= 6 && l4.length >= 4) return `${f6.slice(0, 4)} ${f6.slice(4, 6)}** **** ${l4}`;
  if (l4.length >= 4) return `**** **** **** ${l4}`;
  return '';
}

// Customer vault cards come back as e.g. "411111######1111" — first 6 + masking + last 4.
function formatFromF6L4(raw: string): string {
  const digits = raw.match(/\d/g) ?? [];
  if (digits.length < 10) return '';
  const f6 = digits.slice(0, 6).join('');
  const l4 = digits.slice(-4).join('');
  return `${f6.slice(0, 4)} ${f6.slice(4, 6)}** **** ${l4}`;
}

function fmtCurrency(amount: unknown, currency = 'CAD') {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: String(currency || 'CAD'),
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
  }).format(Number(amount ?? 0));
}

function fmtDateTime(dateStr: unknown): string {
  if (!dateStr) return '—';
  const d = new Date(String(dateStr).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(dateStr);
  return (
    d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })
  );
}

function capitalize(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
}

// Helcim returns bank account fields like "CHECKING"/"PERSONAL" in all caps.
function titleCase(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
}

function fmtExpiry(raw: string): string {
  const clean = raw.replace(/\D/g, '');
  if (clean.length === 4) return `${clean.slice(0, 2)}/${clean.slice(2)}`;
  return raw;
}

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const STATUS_COLOR: Record<string, string> = {
  approved: 'success',
  declined: 'danger',
  refunded: 'warning',
  reversed: 'medium',
};

// Tone-class lookup for badges — ion-badge's default (non `.ion-color`) :host
// rule reads --background/--color directly, so a CSS-module tone class is
// applied instead of Ionic's `color` prop, matching InvoiceList's/
// HelcimPaymentsModal's badge system.
const BADGE_TONE_CLASS: Record<string, string> = {
  success: 'badgeSuccess',
  danger: 'badgeDanger',
  warning: 'badgeWarning',
  medium: 'badgeMedium',
};

const BRAND_MAP: Record<string, string> = {
  VI: 'Visa', MC: 'Mastercard', AX: 'American Express', DI: 'Discover',
};

const BRAND_LOGO: Record<string, string> = {
  VI: '/Visa_Brandmark_Blue_RGB_2021.svg',
  MC: '/ma_symbol.svg',
  AX: '/AXP.svg',
  DI: '/09-12-21_04_20_DGN_AcceptanceMark_FC_Hrz_RGB.svg',
};

const BRAND_LOGO_STYLE: Record<string, React.CSSProperties> = {
  VI: { background: 'white', border: '0.5px solid rgba(0,0,0,0.12)', objectFit: 'contain' },
  MC: { background: '#252525', objectFit: 'cover' },
  AX: { background: 'white', border: '0.5px solid rgba(0,0,0,0.12)', objectFit: 'cover' },
  DI: { background: 'white', border: '0.5px solid rgba(0,0,0,0.1)', objectFit: 'contain' },
};

const AVS_MAP: Record<string, string> = {
  X: 'Exact Match (Address & Zip)', Y: 'Full Match', A: 'Address Match',
  Z: 'Zip Match', N: 'No Match', U: 'Unavailable', R: 'Retry',
  S: 'Not Supported', B: 'Address Not Provided', G: 'International Card',
};

const CVV_MAP: Record<string, string> = {
  M: 'Match', N: 'No Match', P: 'Not Processed',
  S: 'Not Certified', U: 'Not Provided', I: 'Invalid',
};

const Row: React.FC<{ label: string; value: string; copy?: boolean }> = ({ label, value, copy }) => {
  const handleCopy = () => {
    try { navigator.clipboard.writeText(value); } catch { /* silent */ }
  };
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>
        {value || '—'}
        {copy && value && (
          <button className={styles.copyBtn} onClick={handleCopy}>
            <IonIcon icon={copyOutline} />
          </button>
        )}
      </span>
    </div>
  );
};

interface Props {
  tx: Record<string, unknown> | null;
  onClose: () => void;
  onInvoiceClick?: (invoiceNumber: string) => void;
  // customerCode -> Helcim customer's contactName, loaded once by the parent
  // (HelcimPaymentsModal) via getAllHelcimCustomers. cardHolderName is
  // whichever name was on the card at swipe time and can't be trusted to
  // identify the actual customer (e.g. a spouse can pay someone else's
  // invoice), so it's not used for the hero display name.
  customerNames?: Record<string, string>;
}

const HelcimTransactionDetailModal: React.FC<Props> = ({ tx, onClose, onInvoiceClick, customerNames = {} }) => {
  const { t } = useTranslation();
  const [, setCopiedId] = useState(false);
  // null = loading, otherwise the (possibly empty-string) fields found on the customer's saved card.
  // Declined transactions often come back from Helcim without cardHolderName/cardNumber/cardExpiry —
  // this backfills them from the customer's card vault, keyed by cardToken when the tx has one.
  const [fetchedCard, setFetchedCard] = useState<{ expiry: string; holderName: string; number: string } | null>(null);
  // null = loading. The full bank-account record (account type, transit number, etc.) only
  // exists on the customer's bank-accounts list — never on the transaction or invoice — so
  // it's fetched on demand here, matched to this transaction by bankAccountId.
  const [fetchedBank, setFetchedBank] = useState<{ accountType: string; accountCorporate: string; transitNumber: string; businessName: string; holderName: string; accountNumberL4: string } | null>(null);

  const custCode  = tx ? String(tx['customerCode'] ?? '') : '';
  const cardToken = tx ? pick(tx, 'cardToken', 'token') : '';
  // ACH transactions carry bankAccountId/bankAccountL4L4 — fields no card transaction has.
  const isAch = tx ? Boolean(tx['bankAccountId'] || tx['bankAccountL4L4']) : false;
  const bankAccountId = tx ? String(tx['bankAccountId'] ?? '') : '';
  const invoiceCustomerId = tx ? Number(tx['customerId'] ?? 0) : 0;

  // Card-vault lookup only applies to card transactions — the early-out
  // reset is computed during render (React's "adjust state when a
  // dependency changes" pattern) rather than as a synchronous setState call
  // at the top of the effect, which now only handles the actual async
  // lookup.
  const transactionId = tx?.['transactionId'];
  const cardLookupApplies = !!tx && !!custCode && !isAch;
  const [prevCardLookupKey, setPrevCardLookupKey] = useState({ transactionId, cardLookupApplies });
  if (prevCardLookupKey.transactionId !== transactionId || prevCardLookupKey.cardLookupApplies !== cardLookupApplies) {
    setPrevCardLookupKey({ transactionId, cardLookupApplies });
    setFetchedCard(cardLookupApplies ? null : { expiry: '', holderName: '', number: '' });
  }
  useEffect(() => {
    const empty = { expiry: '', holderName: '', number: '' };
    // The card vault lookup only applies to card transactions.
    if (!tx || !custCode || isAch) return;
    getHelcimCustomer(custCode).then(customer => {
      if (!customer) { setFetchedCard(empty); return; }
      const cards = (customer['cards'] as Record<string, unknown>[] | undefined) ?? [];
      const match = cardToken
        ? cards.find(c => String(c['cardToken'] ?? '') === cardToken)
        : cards[0];
      if (!match) { setFetchedCard(empty); return; }
      setFetchedCard({
        expiry: fmtExpiry(String(match['cardExpiry'] ?? '')),
        holderName: String(match['cardHolderName'] ?? match['holderName'] ?? ''),
        number: pick(match, 'cardF6L4', 'cardNumber', 'maskedCardNumber'),
      });
    }).catch(() => setFetchedCard(empty));
  }, [transactionId, tx, custCode, isAch, cardToken]);

  // Same pattern for the ACH bank-account lookup.
  const bankLookupApplies = !!tx && isAch && !!invoiceCustomerId;
  const [prevBankLookupKey, setPrevBankLookupKey] = useState({ transactionId, bankLookupApplies });
  if (prevBankLookupKey.transactionId !== transactionId || prevBankLookupKey.bankLookupApplies !== bankLookupApplies) {
    setPrevBankLookupKey({ transactionId, bankLookupApplies });
    setFetchedBank(bankLookupApplies ? null : { accountType: '', accountCorporate: '', transitNumber: '', businessName: '', holderName: '', accountNumberL4: '' });
  }
  useEffect(() => {
    const empty = { accountType: '', accountCorporate: '', transitNumber: '', businessName: '', holderName: '', accountNumberL4: '' };
    if (!tx || !isAch || !invoiceCustomerId) return;
    getHelcimCustomer(undefined, invoiceCustomerId).then(customer => {
      if (!customer) { setFetchedBank(empty); return; }
      const accounts = (customer['bankAccounts'] as Record<string, unknown>[] | undefined) ?? [];
      const match = bankAccountId
        ? accounts.find(a => String(a['id'] ?? '') === bankAccountId)
        : accounts[0];
      if (!match) { setFetchedBank(empty); return; }
      const address = match['address'] as Record<string, unknown> | undefined;
      setFetchedBank({
        accountType: String(match['accountType'] ?? ''),
        accountCorporate: String(match['accountCorporate'] ?? ''),
        // Helcim's own "transitNumber" API field is reliably empty — the value their
        // dashboard actually labels "Transit Number" comes from routingNumber instead.
        transitNumber: String(match['routingNumber'] || match['transitNumber'] || ''),
        businessName: String(customer['businessName'] ?? ''),
        holderName: String(address?.['name'] ?? ''),
        accountNumberL4: String(match['bankAccountNumberL4'] ?? ''),
      });
    }).catch(() => setFetchedBank(empty));
  }, [transactionId, tx, isAch, invoiceCustomerId, bankAccountId]);

  if (!tx) return null;

  const status        = String(tx['status'] ?? '').toLowerCase();
  const amount        = Number(tx['amount'] ?? tx['totalAmount'] ?? 0);
  const currency      = String(tx['currency'] ?? 'CAD');
  const cardHolderRaw = String(tx['cardHolderName'] ?? '');
  const cardHolder    = cardHolderRaw || (fetchedCard?.holderName ?? '');
  const cardHolderLoading = !cardHolderRaw && fetchedCard === null;
  // Never falls back to cardHolder — see the customerNames prop doc comment.
  const displayName   = customerNames[custCode] || custCode;
  const cardBrand     = pick(tx, 'cardType', 'card_type', 'cardBrand', 'brand');
  const cardNumRaw    = formatCardNumber(tx);
  const cardNum       = cardNumRaw || (fetchedCard?.number ? formatFromF6L4(fetchedCard.number) : '');
  const cardNumLoading = !cardNumRaw && fetchedCard === null;
  const cardExpiry    = (fetchedCard?.expiry ?? '') || fmtExpiry(pick(tx, 'cardExpiry', 'card_expiry', 'expiryDate', 'expiry'));
  const invoiceNum    = pick(tx, 'invoiceNumber', 'invoice_number');
  const txType        = pick(tx, 'type', 'transactionType') || 'purchase';
  const txId          = pick(tx, 'transactionId', 'id');
  const dateTime      = tx['dateCreated'] ?? tx['dateTime'] ?? tx['date'];
  const approvalCode  = pick(tx, 'approvalCode', 'approval_code', 'authCode');
  // batchNumber (backend-resolved "Batch #102" shown in the Helcim dashboard) takes
  // priority over cardBatchId (an unrelated internal ID, e.g. 6698507).
  const batchNum      = pick(tx, 'batchNumber', 'batch', 'batchId', 'cardBatchId');
  const terminal      = pick(tx, 'terminalName', 'terminal', 'terminalId');
  const txUser        = pick(tx, 'userName', 'user', 'helcimUser', 'processedBy');
  const avsResponse   = pick(tx, 'avsResponse', 'avs', 'avsMessage');
  const cvvResponse   = pick(tx, 'cvvResponse', 'cvv', 'cvvMessage');

  const paymentSource = isAch
    ? 'ACH Bank Payment'
    : (invoiceNum.startsWith('SUB-') ? 'Recurring' : 'HelcimPay.js');

  const friendlyBrand = BRAND_MAP[cardBrand.toUpperCase()] ?? cardBrand;

  const heroClass =
    status === 'approved' ? styles.heroApproved :
    status === 'declined' ? styles.heroDeclined :
    status === 'refunded' ? styles.heroRefunded :
    styles.heroDefault;

  const handleCopyId = () => {
    try { navigator.clipboard.writeText(txId); setCopiedId(true); setTimeout(() => setCopiedId(false), 1500); } catch { /* silent */ }
  };

  return (
    <IonModal isOpen={!!tx} onDidDismiss={onClose} className={styles.modal}>
      <BasePageWithHeader
        toolbarClassName={styles.toolbar}
        title=""
        titleNode={
          <div className={styles.titleArea}>
            <IonBadge className={`${styles.statusBadge} ${styles[BADGE_TONE_CLASS[STATUS_COLOR[status] ?? 'medium']]}`}>
              {capitalize(status)}
            </IonBadge>
          </div>
        }
        startButtons={
          <IonButton fill="clear" className={styles.closeBtn} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton fill="clear" className={styles.closeBtn} style={{ visibility: 'hidden' }}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        }
      >
        <IonContent className={styles.content}>
          <IonGrid className={styles.scrollContainer}>

            {/* ── Hero: amount + customer inside ── */}
            <div className={`${styles.amountSection} ${heroClass}`}>
              <span className={styles.amountLabel}>{t('components.HelcimTransactionDetailModal.totalAmount')}</span>
              <span className={styles.amount}>{fmtCurrency(amount, currency)}</span>
              <span className={styles.currencyLabel}>{currency}</span>

              {displayName && (
                <div className={styles.customerCard}>
                  <div className={styles.avatar}>{initials(displayName)}</div>
                  <div className={styles.customerInfo}>
                    <span className={styles.customerName}>{displayName}</span>
                    {custCode && displayName !== custCode && (
                      <span className={styles.customerSub}>{custCode}</span>
                    )}
                  </div>
                  <IonIcon icon={chevronForwardOutline} className={styles.customerChevron} />
                </div>
              )}
            </div>

            {/* ── Invoice + Card/Bank row ── */}
            {(invoiceNum || cardNum || isAch) && (
              <div className={styles.infoRow}>
                {invoiceNum && (
                  <IonCard
                    className={`${styles.infoCard} ${onInvoiceClick ? styles.infoCardClickable : ''}`}
                    onClick={onInvoiceClick ? () => onInvoiceClick(invoiceNum) : undefined}
                  >
                    <IonCardHeader className={styles.infoCardHeader}>
                      <IonCardSubtitle className={styles.infoCardLabel}>{t('components.HelcimTransactionDetailModal.invoice')}</IonCardSubtitle>
                      <IonIcon icon={chevronForwardOutline} className={styles.infoCardChevron} />
                    </IonCardHeader>
                    <IonCardContent className={styles.infoCardBody}>
                      <IonBadge className={`${styles.paidBadge} ${styles.badgeSuccess}`}>{t('components.HelcimTransactionDetailModal.paid')}</IonBadge>
                      <p className={styles.infoCardValue}>{invoiceNum}</p>
                      {!!dateTime && <p className={styles.infoCardSub}>{t('components.HelcimTransactionDetailModal.paidOn')} {fmtDateTime(dateTime).split(' · ')[0]}</p>}
                    </IonCardContent>
                  </IonCard>
                )}
                {isAch ? (
                  (fetchedBank === null || fetchedBank.accountNumberL4) && (
                    <IonCard className={styles.infoCard}>
                      <IonCardHeader className={styles.infoCardHeader}>
                        <IonCardSubtitle className={styles.infoCardLabel}>{t('components.HelcimTransactionDetailModal.bankAccount')}</IonCardSubtitle>
                        <IonIcon icon={chevronForwardOutline} className={styles.infoCardChevron} />
                      </IonCardHeader>
                      <IonCardContent className={styles.infoCardBody}>
                        <FaUniversity style={{ fontSize: 20 }} />
                        <div className={styles.cardNumWrap}>
                          <span className={styles.cardNumSuffix}>
                            {fetchedBank === null ? t('components.HelcimTransactionDetailModal.loading') : `••••${fetchedBank.accountNumberL4}`}
                          </span>
                        </div>
                      </IonCardContent>
                    </IonCard>
                  )
                ) : cardNum && (
                  <IonCard className={styles.infoCard}>
                    <IonCardHeader className={styles.infoCardHeader}>
                      <IonCardSubtitle className={styles.infoCardLabel}>{t('components.HelcimTransactionDetailModal.creditCard')}</IonCardSubtitle>
                      <IonIcon icon={chevronForwardOutline} className={styles.infoCardChevron} />
                    </IonCardHeader>
                    <IonCardContent className={styles.infoCardBody}>
                      {BRAND_LOGO[cardBrand.toUpperCase()] && (
                        <img
                          src={BRAND_LOGO[cardBrand.toUpperCase()]}
                          alt={friendlyBrand}
                          className={styles.cardBrandLogo}
                          style={BRAND_LOGO_STYLE[cardBrand.toUpperCase()]}
                        />
                      )}
                      <div className={styles.cardNumWrap}>
                        <span className={styles.cardNumPrefix}>{cardNum.slice(0, -4)}</span>
                        <span className={styles.cardNumSuffix}>{cardNum.slice(-4)}</span>
                      </div>
                      {cardExpiry && <p className={styles.infoCardSub}>{t('components.HelcimTransactionDetailModal.expiry', { cardExpiry })}</p>}
                    </IonCardContent>
                  </IonCard>
                )}
              </div>
            )}

            {/* ── Accordion sections ── */}
            <IonAccordionGroup className={styles.accordionGroup}>

              <IonAccordion value="txDetails" className={styles.accordion}>
                <IonItem slot="header" lines="none" className={styles.accordionHeader}>
                  <IonLabel>{t('components.HelcimTransactionDetailModal.transactionDetails')}</IonLabel>
                </IonItem>
                <div slot="content" className={styles.accordionContent}>
                  <Row label={t('components.HelcimTransactionDetailModal.timeProcessed')} value={fmtDateTime(dateTime)} />
                  <Row label={t('components.HelcimTransactionDetailModal.type')} value={capitalize(txType)} />
                  <Row label={t('components.HelcimTransactionDetailModal.batch')} value={batchNum || '—'} />
                  <div className={styles.row}>
                    <span className={styles.rowLabel}>{t('components.HelcimTransactionDetailModal.id')}</span>
                    <span className={styles.rowValue}>
                      {txId || '—'}
                      {txId && (
                        <button className={styles.copyBtn} onClick={handleCopyId}>
                          <IonIcon icon={copyOutline} />
                        </button>
                      )}
                    </span>
                  </div>
                  {!isAch && <Row label={t('components.HelcimTransactionDetailModal.terminal')} value={terminal} />}
                  {txUser && <Row label={t('components.HelcimTransactionDetailModal.user')} value={txUser} />}
                  <Row label={t('components.HelcimTransactionDetailModal.paymentSource')} value={paymentSource} />

                </div>
              </IonAccordion>

              <IonAccordion value="paymentMethod" className={styles.accordion}>
                <IonItem slot="header" lines="none" className={styles.accordionHeader}>
                  <IonLabel>{t('components.HelcimTransactionDetailModal.paymentMethod')}</IonLabel>
                </IonItem>
                <div slot="content" className={styles.accordionContent}>
                  {isAch ? (
                    <>
                      <Row label={t('components.HelcimTransactionDetailModal.method')} value="Bank Transfer (ACH)" />
                      <Row label={t('components.HelcimTransactionDetailModal.bankAccountType')} value={fetchedBank === null ? t('components.HelcimTransactionDetailModal.loading') : (titleCase(fetchedBank.accountType) || '—')} />
                      <Row label={t('components.HelcimTransactionDetailModal.personalCorporate')} value={fetchedBank === null ? t('components.HelcimTransactionDetailModal.loading') : (titleCase(fetchedBank.accountCorporate) || '—')} />
                      <Row label={t('components.HelcimTransactionDetailModal.transitNumber')} value={fetchedBank === null ? t('components.HelcimTransactionDetailModal.loading') : (fetchedBank.transitNumber || '—')} copy={!!fetchedBank?.transitNumber} />
                      <Row label={t('components.HelcimTransactionDetailModal.bankAccountNumber')} value={fetchedBank === null ? t('components.HelcimTransactionDetailModal.loading') : (fetchedBank.accountNumberL4 ? `••••${fetchedBank.accountNumberL4}` : '—')} />
                      <Row label={t('components.HelcimTransactionDetailModal.accountHolder')} value={fetchedBank === null ? t('components.HelcimTransactionDetailModal.loading') : (fetchedBank.holderName || cardHolder || '—')} />
                      <Row label={t('components.HelcimTransactionDetailModal.businessName')} value={fetchedBank === null ? t('components.HelcimTransactionDetailModal.loading') : (fetchedBank.businessName || '—')} />
                    </>
                  ) : (
                    <>
                      <Row label={t('components.HelcimTransactionDetailModal.cardType')} value={friendlyBrand || '—'} />
                      <Row label={t('components.HelcimTransactionDetailModal.cardholderName')} value={cardHolderLoading ? t('components.HelcimTransactionDetailModal.loading') : (cardHolder || '—')} />
                      <Row label={t('components.HelcimTransactionDetailModal.cardNumber')} value={cardNumLoading ? t('components.HelcimTransactionDetailModal.loading') : (cardNum || '—')} />
                      <Row label={t('components.HelcimTransactionDetailModal.cardExpiry')} value={fetchedCard === null ? t('components.HelcimTransactionDetailModal.loading') : (cardExpiry || '—')} />
                    </>
                  )}
                </div>
              </IonAccordion>

              <IonAccordion value="verification" className={styles.accordion}>
                <IonItem slot="header" lines="none" className={styles.accordionHeader}>
                  <IonLabel>{t('components.HelcimTransactionDetailModal.verificationResponses')}</IonLabel>
                </IonItem>
                <div slot="content" className={styles.accordionContent}>
                  <Row label={t('components.HelcimTransactionDetailModal.approvalCode')} value={approvalCode || '—'} copy={!!approvalCode} />
                  {!isAch && (
                    <>
                      <Row label={t('components.HelcimTransactionDetailModal.avsCheck')} value={avsResponse ? (AVS_MAP[avsResponse.toUpperCase()] ?? avsResponse) : '—'} />
                      <Row label={t('components.HelcimTransactionDetailModal.cvvCheck')} value={cvvResponse ? (CVV_MAP[cvvResponse.toUpperCase()] ?? cvvResponse) : '—'} />
                    </>
                  )}
                </div>
              </IonAccordion>

            </IonAccordionGroup>

            <div style={{ height: 40 }} />
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>
    </IonModal>
  );
};

export default HelcimTransactionDetailModal;
